import { err, ok, Result } from '@easeltv/fail';
import { chunkArray } from '@easeltv/utilities';
import { ZodType, ZodTypeDef } from 'zod';

import { ResponseSchemaInvalidFail } from '../errors';
import { RequestResult } from './types';

/**
 * validateSchema
 *
 * Attempts to validate the given data against the schema
 * Returns a ResponseSchemaInvalidFail if unsuccessful
 *
 * @param schema
 * @param data
 * @returns
 */
export const validateSchema = <
    Output,
    Def extends ZodTypeDef = ZodTypeDef,
    Input = Output,
>(
    schema: ZodType<Output, Def, Input>,
    data: unknown,
): Result<Output, ResponseSchemaInvalidFail> => {
    const parseResult = schema.safeParse(data);

    if (!parseResult.success)
        return err(
            new ResponseSchemaInvalidFail({
                key: 'remote.schema.mismatch',
                name: 'Client Schema Mismatch',
                description:
                    'Response returned does not match schema after filter',

                data: {
                    response: data,
                    error: parseResult.error,
                },
            }),
        );

    return ok(parseResult.data);
};

/**
 * arrayToCSV
 *
 * Joins arrays of strings or numbers to comma separated string representations of themselves
 *
 * @param option A type that needs to be converted to a string, and maybe its an array
 * @returns string value
 */
export const arrayToCSV = (option: unknown): string => {
    if (Array.isArray(option)) {
        return option.join();
    }

    /** Uses string constructor rather than .toString() method for cases like below
     *  String(null);     // "null"
     * value.toString(); // TypeError
     *
     * String constructor will call .toString() on any object that has the method defined.
     * See more https://stackoverflow.com/questions/3945202
     */
    return String(option);
};

/**
 * mapOptionsToStrings
 *
 * API requires query parameters to be string values, this function will return a map of the options object with all
 * values for the keys converted to strings (if necessary)
 *
 * @param options Object containing values to be added to the query string
 * @returns Object with string values
 */
export const mapOptionsToStrings = (options?: {
    [key: string]: unknown;
}): { [key: string]: string } =>
    Object.entries(options ?? {}).reduce(
        (obj, [key, value]) =>
            value !== undefined
                ? {
                      [key]: arrayToCSV(value),
                      ...obj,
                  }
                : obj,
        {},
    );

/**
 * chunkBulkRequest
 *
 * Makes sure the IDs passed to
 *
 * @param ids - Array of IDs to fetch
 * @param fetcher - SDK bulk request to chunk
 * @returns
 */
export const chunkBulkRequest = async <T>(
    ids: string[],
    fetcher: ({
        ids,
    }: {
        ids: string[];
    }) => Promise<RequestResult<{ data: T[] }>>,
) => {
    const idsChunks = Array.from(chunkArray(ids));

    const promises = idsChunks.map((chunk) =>
        chunk.length
            ? fetcher({ ids: Array.from(chunk) })
            : Promise.resolve(ok({ status: 200, body: { data: [] } })),
    );

    const results = await Promise.all(promises);

    return results.reduce<{ data: T[]; complete: boolean }>(
        (sum, result, index) => {
            if (!result.ok) {
                // @TODO: Error Handling
                // eslint-disable-next-line no-console
                console.log(`Chunk ${index} failed:`, result.payload);

                return {
                    ...sum,
                    complete: false,
                };
            }

            return {
                ...sum,
                data: [...sum.data, ...result.payload.body.data],
            };
        },
        { data: [], complete: true },
    );
};
